// panels/pvc/PvcControl.js —— 光储充 · 3.5 控制中心（需求文档 p20–p30）
// 3.5.1 策略参数：策略状态 / 光伏调节参数（差异 #10）/ 需量保护 / 逆功率保护 / 电池保护 / 储能电池低电保护（差异 #10）
// 3.5.2 计划曲线：预置策略模板、新增(+)、编辑（充放电功率 / PCS 关机·待机 / 均分规则）、按日期配置、批量编辑、循环方式
//               —— 差异 #2 / #3：策略功率来源为控制中心「计划曲线」，以虚线（预测值）展示
// 3.5.3 设备控制（差异 #8：设备控制放在控制中心）：PCS / BMS / 空调 / 液冷机组 / 光伏逆变器
// 口令 gate 由外层控制台传入，本面板不实现口令逻辑。
import React, { useState } from 'react';
import ScrollTabs from '../../../../components/ScrollTabs';
import { Radio, Checkbox, Space, Button, Select, InputNumber, Table, Modal, Tag, Input, Segmented, Alert, message,
} from 'antd';
import { PlusOutlined, PlayCircleOutlined, PauseCircleOutlined, SettingOutlined, ThunderboltOutlined } from '@ant-design/icons';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import {
  getProtection, getPresetStrategy, getStrategyCurve, getAdvancedFeatures, getDevices, getControlActions, getWritePoints,
} from '../../../../config/stationData';
import { TRANSFORMERS } from './data';
import './pvc.css';

const AXIS = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const TT = { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff' };
const GRID = 'rgba(255,255,255,0.08)';
const DASH = '5 4';

const PvcControl = ({ station }) => {
  const prot = getProtection();
  const adv = getAdvancedFeatures();
  const dev = getDevices(station.id);

  // ---------- 3.5.1 策略参数 ----------
  const [strategyState, setStrategyState] = useState('off'); // off=未启用 / on=已启用（回显延迟 3~8s）
  const [stopAction, setStopAction] = useState('pcs-hold');
  const [demandAction, setDemandAction] = useState('charge-limit-idle');
  const [reverseAction, setReverseAction] = useState('discharge-limit-idle');
  const [demandTfs, setDemandTfs] = useState(['T-01']);
  const [reverseTfs, setReverseTfs] = useState(['T-01']);
  const [batteryExt, setBatteryExt] = useState(['temp']); // 温度 / 电压（可选）
  const [lowPowerOn, setLowPowerOn] = useState(false);

  // ---------- 3.5.2 计划曲线 ----------
  const presets = getPresetStrategy();
  const strategyCurve = getStrategyCurve();
  const [template, setTemplate] = useState('默认计划曲线');
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [dateMode, setDateMode] = useState('today');
  const [cycle, setCycle] = useState('每日循环');

  // ---------- 3.5.3 设备控制 ----------
  const actions = getControlActions(station);
  const [writeGroup, setWriteGroup] = useState(null);
  const writePoints = writeGroup ? getWritePoints(writeGroup) : [];

  const paramTab = (
    <>
      <div className="panel">
        <div className="panel-title">策略状态<small>云端 / 站端 EMS 策略状态 · 停止 / 开启策略</small></div>
        <div className="strat-grid">
          <div className="strat-item"><div className="kpi-label">云端 EMS 策略</div><div className="kpi-value" style={{ color: strategyState === 'on' ? '#52e8b0' : '#ff5a7f' }}>{strategyState === 'on' ? '预置计划曲线-已启用' : '未启用'}</div><div className="kpi-foot">{strategyState === 'on' ? '云端执行中 · 与站端同步' : '策略未启用'}</div></div>
          <div className="strat-item"><div className="kpi-label">站端 EMS 策略</div><div className="kpi-value" style={{ color: strategyState === 'on' ? '#52e8b0' : '#ff5a7f' }}>{strategyState === 'on' ? '已启用' : '未启用'}</div><div className="kpi-foot">{strategyState === 'on' ? '与云端同步' : '策略未启用'}</div></div>
          <div className="strat-item"><div className="kpi-label">当前 PCS</div><div className="kpi-value">{dev.pcs.filter((p) => p.running).length}<span className="unit">/ {dev.pcs.length}</span></div><div className="kpi-foot">运行台数</div></div>
        </div>
        <div className="pvc-param-row">
          <span className="dev-kv-k">停止策略后动作</span>
          <Radio.Group value={stopAction} onChange={(e) => setStopAction(e.target.value)}>
            <Radio value="all-stop">全站 PCS 停机</Radio>
            <Radio value="pcs-hold">全站 PCS 保持当前功率运行</Radio>
            <Radio value="inv-stop">所有光伏逆变器停机</Radio>
            <Radio value="inv-hold">所有光伏逆变器保持当前功率运行</Radio>
          </Radio.Group>
        </div>
        <Space>
          <Button danger icon={<PauseCircleOutlined />} disabled={strategyState !== 'on'} onClick={() => { setStrategyState('off'); message.warning('策略已停止，执行所选停止后动作'); }}>停止策略</Button>
          <Button type="primary" icon={<PlayCircleOutlined />} disabled={strategyState === 'on'} onClick={() => { setStrategyState('on'); message.success('策略已启用：云端/站端状态已更新为「预置计划曲线-已启用」（回显延迟 3~8 秒）'); }}>开启策略</Button>
        </Space>
      </div>

      {/* 差异 #10：光伏调节参数 */}
      <div className="panel">
        <div className="panel-title">光伏调节参数<small>光储充类型独有 · 每个光伏节点分别配置</small></div>
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={dev.pv}
          columns={[
            { title: '光伏节点', dataIndex: 'name' },
            { title: '启停灵敏度下限 (%)', render: () => <InputNumber size="small" min={0} max={100} defaultValue={2} /> },
            { title: '功率拉升指数', render: () => <InputNumber size="small" min={0} max={5} step={0.1} defaultValue={1.2} /> },
            { title: '功率降低指数', render: () => <InputNumber size="small" min={0} max={5} step={0.1} defaultValue={0.8} /> },
            { title: '限功率 (kW)', render: (_, r) => <InputNumber size="small" min={0} defaultValue={r.dcPower * 6} /> },
          ]}
        />
        <div className="dev-sub">默认值取自保护参数：限制功率 {prot.pvRegulate.limitPower} kW · 变化率 {prot.pvRegulate.ramp} %/min</div>
      </div>

      <div className="grid-2-even">
        <div className="panel">
          <div className="panel-title">需量保护<small>勾选受保护的变压器 · 触发动作</small></div>
          <Checkbox.Group value={demandTfs} onChange={(v) => setDemandTfs(v)} style={{ display: 'block' }}>
            {TRANSFORMERS.map((t) => (
              <div className="pvc-param-row" key={t.key}>
                <Checkbox value={t.key}>{t.name}</Checkbox>
                <span style={{ color: 'rgba(255,255,255,0.45)' }}>{t.detail}</span>
              </div>
            ))}
          </Checkbox.Group>
          <div className="pvc-param-row">
            <span className="dev-kv-k">需量动作</span>
            <Radio.Group value={demandAction} onChange={(e) => setDemandAction(e.target.value)}>
              <Radio value="charge-limit-idle">降低充电功率直到静置</Radio>
              <Radio value="charge-limit-discharge">降低充电功率，必要时放电</Radio>
            </Radio.Group>
          </div>
          <div className="dev-sub">限制值 {prot.demand.limit} kW · 默认动作 {prot.demand.action}</div>
        </div>

        <div className="panel">
          <div className="panel-title">逆功率保护<small>勾选受保护的变压器 · 触发动作</small></div>
          <Checkbox.Group value={reverseTfs} onChange={(v) => setReverseTfs(v)} style={{ display: 'block' }}>
            {TRANSFORMERS.map((t) => (
              <div className="pvc-param-row" key={t.key}>
                <Checkbox value={t.key}>{t.name}</Checkbox>
                <span style={{ color: 'rgba(255,255,255,0.45)' }}>{t.detail}</span>
              </div>
            ))}
          </Checkbox.Group>
          <div className="pvc-param-row">
            <span className="dev-kv-k">逆功率动作</span>
            <Radio.Group value={reverseAction} onChange={(e) => setReverseAction(e.target.value)}>
              <Radio value="discharge-limit-idle">降低放电功率直到静置</Radio>
              <Radio value="discharge-limit-charge">降低放电功率，必要时充电</Radio>
            </Radio.Group>
          </div>
          <div className="dev-sub">限制值 {prot.reverse.limit} kW · 默认动作 {prot.reverse.action}</div>
        </div>
      </div>

      <div className="grid-2-even">
        <div className="panel">
          <div className="panel-title">电池保护<small>SOC 保护（必选）· 单体温度 / 单体电压（可选）</small></div>
          <div className="pvc-param-row">
            <span className="dev-kv-k">SOC 上限（≥ 禁充）</span>
            <InputNumber size="small" min={0} max={100} defaultValue={prot.battery.socHigh} addonAfter="%" />
          </div>
          <div className="pvc-param-row">
            <span className="dev-kv-k">SOC 下限（≤ 禁放）</span>
            <InputNumber size="small" min={0} max={100} defaultValue={prot.battery.socLow} addonAfter="%" />
          </div>
          <div className="pvc-param-row">
            <span className="dev-kv-k">扩展保护</span>
            <Checkbox.Group value={batteryExt} onChange={(v) => setBatteryExt(v)}>
              <Checkbox value="temp">单体温度（{prot.battery.cellTempLow} ~ {prot.battery.cellTempHigh} ℃）</Checkbox>
              <Checkbox value="voltage">单体电压（{prot.battery.cellVoltageLow} ~ {prot.battery.cellVoltageHigh} V）</Checkbox>
            </Checkbox.Group>
          </div>
          <div className="dev-sub">规则：SOC ≥ 上限禁充；SOC ≤ 下限禁放</div>
        </div>

        {/* 差异 #10：储能电池低电保护 */}
        <div className="panel">
          <div className="panel-title">储能电池低电保护<small>光储充类型独有 · SOC 低于阈值强制启动充电，补充少量后自动停止</small></div>
          <div className="pvc-param-row">
            <span className="dev-kv-k">启用</span>
            <Radio.Group value={lowPowerOn ? 'on' : 'off'} onChange={(e) => setLowPowerOn(e.target.value === 'on')}>
              <Radio value="on">启用</Radio>
              <Radio value="off">停用</Radio>
            </Radio.Group>
          </div>
          <div className="pvc-param-row">
            <span className="dev-kv-k">触发 SOC 阈值</span>
            <InputNumber size="small" min={0} max={100} defaultValue={8} addonAfter="%" />
          </div>
          <div className="pvc-param-row">
            <span className="dev-kv-k">补电目标 SOC</span>
            <InputNumber size="small" min={0} max={100} defaultValue={15} addonAfter="%" />
          </div>
          <div className="pvc-param-row">
            <span className="dev-kv-k">补电功率</span>
            <InputNumber size="small" min={0} defaultValue={adv.lowPower.power} addonAfter="kW" />
          </div>
          <div className="dev-sub">默认参考：单体电压 {adv.lowPower.voltage} V 触发 · 补至 {adv.lowPower.target} V</div>
        </div>
      </div>
    </>
  );

  const planTab = (
    <>
      <div className="panel">
        <div className="panel-title">
          <span>计划曲线<small style={{ marginLeft: 8 }}>策略功率来源 · 虚线为计划（预测）值 · 充为正 / 放为负</small></span>
          <Space>
            <Select size="small" value={template} onChange={setTemplate} style={{ width: 170 }}
              options={[{ value: '默认计划曲线', label: '默认计划曲线' }, { value: '夏季削峰', label: '夏季削峰' }, { value: '光储优先', label: '光储优先' }]} />
            <Button size="small" icon={<PlusOutlined />}>新增</Button>
          </Space>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={strategyCurve} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
            <XAxis dataKey="clock" interval={3} tick={AXIS} axisLine={false} tickLine={false} />
            <YAxis tick={AXIS} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={TT} />
            <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }} iconSize={8} />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.35)" />
            <Line type="stepAfter" dataKey="power" name="储能计划曲线（预测值）" stroke="#00d4ff" strokeDasharray={DASH} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
        <Alert type="info" showIcon style={{ marginTop: 12 }} title="运行曲线中的「储能策略功率」取自本计划曲线，并以虚线形式作为预测值展示（差异项）。" />
      </div>

      <div className="panel">
        <div className="panel-title">
          <span>策略模板<small style={{ marginLeft: 8 }}>按日期配置 · 批量编辑 · 循环方式</small></span>
          <Space>
            <Segmented size="small" value={dateMode} onChange={setDateMode} options={[{ value: 'today', label: '今日' }, { value: 'tomorrow', label: '明日' }, { value: 'week', label: '本周' }]} />
            <Select size="small" value={cycle} onChange={setCycle} style={{ width: 130 }} options={[{ value: '每日循环', label: '每日循环' }, { value: '工作日', label: '工作日' }, { value: '周末', label: '周末' }]} />
            <Button size="small">批量编辑</Button>
          </Space>
        </div>
        <Table
          size="small"
          rowKey="key"
          pagination={false}
          dataSource={presets}
          columns={[
            { title: '时段', render: (_, r) => `${r.start} - ${r.end}` },
            { title: '功率 (kW)', dataIndex: 'power', render: (v) => <span style={{ color: v > 0 ? '#00d4ff' : v < 0 ? '#52e8b0' : 'rgba(255,255,255,0.6)' }}>{v}</span> },
            { title: '状态', dataIndex: 'state', render: (v) => <Tag color={v === '充电' ? 'cyan' : v === '放电' ? 'green' : 'default'}>{v}</Tag> },
            { title: '功率均分规则', dataIndex: 'rule' },
            { title: '参与设备', dataIndex: 'pcs' },
            { title: '操作', render: (_, r) => <Button type="link" size="small" icon={<SettingOutlined />} onClick={() => { setEditRow(r); setEditOpen(true); }}>编辑</Button> },
          ]}
        />
      </div>
    </>
  );

  const deviceTab = (
    <>
      {actions.map((a) => (
        <div className="panel" key={a.group}>
          <div className="panel-title">{a.group}<small>设备控制动作 · 策略运行中下发会冲突，须先停止策略</small></div>
          <Space wrap>
            {a.items.map((it) => (
              <Button key={it} size="small">{it}</Button>
            ))}
            {a.group === '光伏逆变器' ? (
              <Button size="small" type="primary" icon={<ThunderboltOutlined />} onClick={() => setWriteGroup('光伏逆变器')}>配置单次下发光伏功率</Button>
            ) : null}
            {['空调', '液冷机组', '光伏逆变器'].includes(a.group) ? (
              <Button size="small" icon={<SettingOutlined />} onClick={() => setWriteGroup(a.group)}>参数配置</Button>
            ) : null}
          </Space>
        </div>
      ))}
      <div className="dev-grid">
        {dev.pcs.map((p) => (
          <div className={`dev-card${p.alarm ? ' fault' : ''}`} key={p.id}>
            <div className="dev-card-head"><span className="dev-name">{p.name}</span><span className="dev-status"><span className={`dot ${p.alarm ? 'fault' : 'online'}`} />{p.alarm || (p.running ? '运行' : '停机')}</span></div>
            <div className="dev-kv">
              <div className="dev-kv-row"><span className="dev-kv-k">并网状态</span><span className="dev-kv-v">{p.grid}</span></div>
              <div className="dev-kv-row"><span className="dev-kv-k">有功功率</span><span className="dev-kv-v">{p.p} <span className="unit">kW</span></span></div>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <>
      <ScrollTabs
        items={[
          { key: 'param', label: '策略参数', children: paramTab },
          { key: 'plan', label: '计划曲线', children: planTab },
          { key: 'device', label: '设备控制', children: deviceTab },
        ]}
      />

      <Modal
        title={`编辑计划曲线${editRow ? ` · ${editRow.start}-${editRow.end}` : ''}`}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={() => setEditOpen(false)}
        destroyOnHidden
      >
        {editRow ? (
          <>
            <div className="pvc-param-row">
              <span className="dev-kv-k">充放电功率</span>
              <InputNumber size="small" defaultValue={editRow.power} addonAfter="kW（充为正 / 放为负）" style={{ width: 220 }} />
            </div>
            <div className="pvc-param-row">
              <span className="dev-kv-k">PCS 关机 / 待机</span>
              <Radio.Group defaultValue={editRow.power === 0 ? 'standby' : 'run'}>
                <Radio value="run">按功率运行</Radio>
                <Radio value="standby">待机（功率 = 0）</Radio>
                <Radio value="off">关机（功率 = 0）</Radio>
              </Radio.Group>
            </div>
            <div className="pvc-param-row">
              <span className="dev-kv-k">功率均分规则</span>
              <Radio.Group defaultValue="available">
                <Radio value="available">按可用 PCS 数量均分</Radio>
                <Radio value="total">按总计 PCS 数量均分</Radio>
              </Radio.Group>
            </div>
            <div className="pvc-param-row">
              <span className="dev-kv-k">模板名称</span>
              <Input size="small" defaultValue={template} style={{ width: 220 }} />
            </div>
          </>
        ) : null}
      </Modal>

      <Modal
        title={`参数配置 · ${writeGroup || ''}`}
        open={!!writeGroup}
        onCancel={() => setWriteGroup(null)}
        footer={<Space><Button onClick={() => setWriteGroup(null)}>取消</Button><Button type="primary" onClick={() => setWriteGroup(null)}>下发</Button></Space>}
        width={620}
        destroyOnHidden
      >
        <Table
          size="small"
          rowKey="name"
          pagination={false}
          dataSource={writePoints}
          columns={[
            { title: '参数', dataIndex: 'name' },
            { title: '当前值', render: (_, r) => `${r.current} ${r.unit}` },
            { title: '范围', dataIndex: 'range' },
            { title: '目标值', render: (_, r) => <Input size="small" defaultValue={r.target} style={{ width: 120 }} /> },
          ]}
        />
      </Modal>
    </>
  );
};

export default PvcControl;
